import { DateRange, RelativeDateRange } from '@logz-pkg/utils';
import { Container, Group, Input, Select, Text } from '@logz-ui/styleguide';
import React, { useCallback, useMemo } from 'react';
import { getDisplayPartsFromDateRange } from '../utils/time-calculations';
import { SELECT_STYLES, TIME_UNIT_OPTIONS } from './constants';
import { createDateRangeFromManualInput, parseRelativeTimeToManualInput } from './utils';

interface IManualInputSectionProps {
  value?: DateRange;
  onChange: (dateRange: DateRange) => void;
  timezone?: string;
}

export const ManualInputSection: React.FC<IManualInputSectionProps> = ({ value, onChange, timezone }) => {
  const { value: manualValue, unit: manualUnit } = useMemo(() => {
    if (value && value.start.type === 'relative' && typeof value.start.value === 'string') {
      return parseRelativeTimeToManualInput(value.start.value) ?? { value: '', unit: 'minutes' };
    }

    return { value: '', unit: 'minutes' };
  }, [value]);

  const selectValue = manualValue === '' ? null : { value: manualUnit, label: manualUnit };
  const { mainPart, timePart } = useMemo(() => getDisplayPartsFromDateRange(value ?? null, timezone), [value, timezone]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      let numValue = parseInt(inputValue, 10);

      numValue = numValue <= 0 ? 1 : numValue;
      numValue = isNaN(numValue) ? 0 : numValue;

      const newDateRange = createDateRangeFromManualInput({ value: numValue, unit: manualUnit }) as RelativeDateRange;

      onChange(newDateRange);
    },
    [manualUnit, onChange],
  );

  const handleSelectChange = useCallback(
    (option: { value: string; label: string }) => {
      const numericValue = typeof manualValue === 'string' ? '' : manualValue;

      const newDateRange = createDateRangeFromManualInput({
        value: numericValue,
        unit: option.value,
      }) as RelativeDateRange;

      onChange(newDateRange);
    },
    [manualValue, onChange],
  );

  return (
    <>
      <Container variant="pLight" p={3} fullWidth display="flex">
        <Group gap={6} alignItems="baseline" fullWidth>
          <Text>Last</Text>
          <Input
            type="number"
            value={manualValue === 0 ? '' : String(manualValue)}
            onChange={handleInputChange}
            onBlur={() => {
              if (manualValue === 0) {
                onChange(createDateRangeFromManualInput({ value: 1, unit: manualUnit }) as RelativeDateRange);
              }
            }}
            width={80}
            mb={0}
            min={0}
            placeholder="0"
          />
          <Select
            value={selectValue}
            onChange={handleSelectChange}
            options={TIME_UNIT_OPTIONS}
            width={120}
            styles={SELECT_STYLES}
            placeholder="Select unit"
            isClearable={false}
            isSearchable={false}
            // Keep the menu out of the popover. Inside it, react-select 5 measures the space below against the
            // popover and opens the menu upward, where the popover clips it. A "fixed" menu does not help either,
            // because the popover transform moves it.
            attachToBody
          />
        </Group>
      </Container>

      {mainPart && (
        <Group gap={1} alignItems="baseline">
          <Text size={14} color="gray.900" weight={700}>
            {mainPart}
          </Text>
          {timePart && (
            <Text size={12} color="gray.800" weight={500}>
              {timePart}
            </Text>
          )}
        </Group>
      )}
    </>
  );
};
